import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * SSR-safe `window.matchMedia` hook. Returns `false` on the server and during
 * the first client paint (so server and client markup agree), then reflects the
 * real match after mount. Replaces bricks-next `useMedia()` for migrated atoms
 * components, which style breakpoints via Tailwind classes but still need a JS
 * boolean for conditional rendering.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setMatches(mediaQueryList.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQueryList.addEventListener('change', onChange);
    return () => mediaQueryList.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

/** bricks `useMedia().smallerThanMD` — viewport ≤ 767px (below the 768px MD breakpoint). */
export const useSmallerThanMD = (): boolean => useMediaQuery('(max-width: 767px)');

/** bricks `useMedia().smallerThanLG` — viewport ≤ 1200px (below the 1201px LG breakpoint). */
export const useSmallerThanLG = (): boolean => useMediaQuery('(max-width: 1200px)');

/** The exact complement of `useSmallerThanMD`, so fractional widths never match both or neither. */
const DESKTOP_QUERY = 'not all and (max-width: 767px)';

// React 18 renders a component again with `getSnapshot` when it updates its own state during
// the hydration render, so until hydration ends the client snapshot is the server value too.
// Otherwise a desktop match reaches the hydration pass and the markup no longer matches.
let hydrated = false;
const hydrationListeners = new Set<() => void>();

const subscribeToDesktopQuery = (onChange: () => void) => {
  const mediaQueryList = window.matchMedia(DESKTOP_QUERY);
  mediaQueryList.addEventListener('change', onChange);
  hydrationListeners.add(onChange);
  return () => {
    mediaQueryList.removeEventListener('change', onChange);
    hydrationListeners.delete(onChange);
  };
};

const matchesDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;
const getServerTrue = () => true;
const getServerFalse = () => false;
const getClientOrServerTrue = () => (hydrated ? matchesDesktop() : true);
const getClientOrServerFalse = () => (hydrated ? matchesDesktop() : false);

/**
 * Ends the server value period of `useIsDesktopViewport`. The app calls it when it mounts, so a
 * page that mounts later through client navigation reads the media query on its first render.
 */
export const markViewportHydrated = () => {
  if (hydrated) return;
  hydrated = true;
  hydrationListeners.forEach((listener) => listener());
};

/**
 * Whether the viewport is at least the MD breakpoint. The server and every
 * render before hydration ends see `serverValue`; after that the hook reads
 * the media query synchronously and follows its changes. Desktop-only work
 * gated on the `false` server value never runs on phones, because a phone
 * never renders a `true` value.
 */
export const useIsDesktopViewport = (serverValue: boolean): boolean => {
  const isDesktop = useSyncExternalStore(
    subscribeToDesktopQuery,
    serverValue ? getClientOrServerTrue : getClientOrServerFalse,
    serverValue ? getServerTrue : getServerFalse,
  );
  // A fallback for roots that the app does not mount, such as tests.
  useEffect(markViewportHydrated, []);
  return isDesktop;
};
